import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ExportJsonExcel from 'js-export-excel';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Button } from 'antd';
import formatPrice from '~helpers/currency';
import ListingTypeNotice from 'common/components/listingTypeNotice';
import { listingTypeNoticeHint, listingTypeNoticeContent } from 'common/constants/reconciliation';
import { LeadGenerationEmpty } from 'common/components/svgs';
import { dayFormatType, formatDay } from '~helpers/day';
import { QUERY_OFFLINE_PAYMENT_ORDER_RECEIVABLES_LIST } from './queries';
import OrderFilter from './components/filter';
import OrderTable from './components/table';
import UpdateStatusModal from './components/modal';

export default function Order() {
  const { t } = useTranslation();
  const history = useHistory();

  // Hide export button for now
  const hideExportButton = true;

  const [offLinePaymentBookingData, setOfflinePaymentBookingData] = useState({});
  const [filters, setFilters] = useState({});
  const [isExportQuery, setIsExportQuery] = useState(false);

  const updateStatusModalRef = useRef(null);

  const [queryOfflinePaymentOrderReceivablesList, { loading }] = useLazyQuery(
    QUERY_OFFLINE_PAYMENT_ORDER_RECEIVABLES_LIST,
    {
      fetchPolicy: 'no-cache',
      onCompleted: res => {
        if (res?.listOfflinePaymentOrderReceivables) {
          if (isExportQuery && res.listOfflinePaymentOrderReceivables.totalCount > 0) {
            handleExportCurrentExcel(res.listOfflinePaymentOrderReceivables);
          } else {
            setOfflinePaymentBookingData(res.listOfflinePaymentOrderReceivables);
          }
        }
      },
    },
  );

  const handleOpenUpdateModal = row => {
    updateStatusModalRef.current?.handleOpenModal(row);
  };

  useEffect(() => {
    queryOfflinePaymentOrderReceivablesList();
  }, []);

  const handleExportCurrentExcel = data => {
    const sheetHeader = [
      'Booking Number',
      'Student Name',
      'Email Address',
      'City',
      'Landlord Name',
      'Property Name',
      'Booking Created Date',
      'Booking Complete Date',
      'Move-in Date',
      'Move-out Date',
      'Room Name',
      'Unit Price',
      'Total Rent',
      'Total Commission',
      'Commission Tier',
      'Current Status',
      'Invoice Number',
      'Invoiced at',
      'Notes',
      'Listing Plan',
    ];

    let xlsxData = data.edges.map(data => ({
      bookingNumber: data?.order?.referenceId,
      studentName: `${data?.order?.student?.passportFirstName} ${data?.order?.student?.passportLastName}`,
      emailAddress: data?.order?.student?.emailAddress,
      city: data?.order?.city?.name,
      landlordName: data?.landlord?.name,
      propertyName: data?.property?.name,
      bookingCreatedDate: data?.order?.createdAt
        ? formatDay(dayFormatType.UKFORMAT, data?.order?.createdAt)
        : '-',
      bookingCompleteDate: data?.order?.paidAt
        ? formatDay(dayFormatType.UKFORMAT, data?.order?.paidAt)
        : '-',
      moveInDate: data?.order?.moveInDate
        ? formatDay(dayFormatType.UKFORMAT, data?.order?.moveInDate)
        : '-',
      moveOutDate: data?.order?.moveOutDate
        ? formatDay(dayFormatType.UKFORMAT, data?.order?.moveOutDate)
        : '-',
      roomName: data?.order?.unitTypeName,
      unitPrice: data?.order?.listingMinPrice,
      totalRent: formatPrice(data?.order?.totalRental, data?.order?.currency),
      totalCommission: formatPrice(data?.commission?.totalPrice, data?.commission?.currency),
      commissionTier:
        (data?.commission?.commissionTierValue &&
        data?.commission?.commissionTierType === 'PERCENTAGE'
          ? data?.commission?.commissionTierValue + '%'
          : data?.commission?.commissionTierValue) || '-',
      currentStatus: t(
        `cms.reconciliation.lead_generation.table.filter.status.${data?.orderReceivableProcesses?.[0]?.currentStatus?.toLowerCase()}`,
      ),
      invoiceNumber: data?.invoiceNumber,
      invoicedAt: data?.invoicedAt ? formatDay(dayFormatType.UKFORMAT, data?.invoicedAt) : '-',
      notes: data?.orderReceivableProcesses?.[0]?.note ?? '-',
      bookingJourney: data?.order?.bookingJourney
        ? t(
            `cms.reconciliation.lead_generation.table.filter.status.${data?.order?.bookingJourney?.toLowerCase()}`,
          )
        : '-',
    }));
    const option = {};
    const filename =
      filters?.bookingJourneys?.length === 1 && filters?.bookingJourneys?.[0]
        ? '-' +
          t(
            `cms.reconciliation.lead_generation.table.filter.status.${filters?.bookingJourneys?.[0]?.toLowerCase()}`,
          )
        : '';
    option['fileName'] = `Reconciliation Report${filename}`;

    option['datas'] = [
      {
        sheetData: xlsxData,
        sheetHeader,
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
    setIsExportQuery(false);
  };

  return (
    <div className="lead-generation-wrapper">
      <header className="header">
        <span className="title">{t('cms.reconciliation.lead_generation.header.title')}</span>
        <ListingTypeNotice
          t={t}
          button={listingTypeNoticeHint}
          content={listingTypeNoticeContent}
        />
        <If
          condition={
            !hideExportButton &&
            ((!loading && offLinePaymentBookingData) || (loading && isExportQuery))
          }
        >
          <Button
            icon="export"
            loading={loading && isExportQuery}
            disabled={offLinePaymentBookingData?.totalCount === 0}
            onClick={() => {
              setIsExportQuery(true);
              queryOfflinePaymentOrderReceivablesList({
                variables: {
                  ...filters,
                  pageSize: offLinePaymentBookingData.totalCount,
                  pageNumber: 1,
                },
              });
            }}
          >
            {t('cms.reconciliation.lead_generation.header.export_desc')}
          </Button>
        </If>
      </header>
      <section className="main-section">
        <Choose>
          <When condition={offLinePaymentBookingData}>
            <OrderFilter
              t={t}
              filters={filters}
              setFilters={setFilters}
              queryList={queryOfflinePaymentOrderReceivablesList}
            />
            <OrderTable
              t={t}
              history={history}
              list={offLinePaymentBookingData}
              queryList={queryOfflinePaymentOrderReceivablesList}
              handleOpenUpdateModal={handleOpenUpdateModal}
              loading={loading && !isExportQuery}
              filters={filters}
              setFilters={setFilters}
            />
          </When>
          <Otherwise>
            <div className="empty">
              <LeadGenerationEmpty className="empty-pic" />
              <span className="empty-desc">
                {t('cms.reconciliation.lead_generation.empty.desc')}
              </span>
            </div>
          </Otherwise>
        </Choose>
      </section>

      <UpdateStatusModal
        refetchData={() => {
          setFilters({});
          queryOfflinePaymentOrderReceivablesList();
        }}
        wrappedComponentRef={form => (updateStatusModalRef.current = form)}
      />
    </div>
  );
}
